import { useEffect } from 'react';
import type { TtpcModulationServiceRootResponse } from 'src/apis/ttpc-modulation-service/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { handleErrorWithNotification } from 'src/utils/handle-error';

type ExecuteProps = {
  ttpcModulationService: string;
};

export function useTtpcModulationService() {
  const request = useAxios(
    (axios, baseConfig, { ttpcModulationService }: ExecuteProps) =>
      axios.request<TtpcModulationServiceRootResponse>({
        ...baseConfig,
        url: ttpcModulationService,
      }),
    { neededOnPageLoad: true, cacheKey: 'ttpc-modulation-service-root' }
  );
  const ttpcModulationService = PRISMA_CONFIG.ttpcModulationService;

  useEffect(() => {
    if (!ttpcModulationService) return;
    request.execute({ ttpcModulationService });
    // oxlint-disable-next-line exhaustive-deps
  }, [ttpcModulationService]);

  return request;
}

export type TtpcModulationServiceRequest = ReturnType<
  typeof useTtpcModulationService
>;

/**
 * Use this, if you can be unauthenticated and if a 401 failure should not
 * block the page from rendering.
 */
export function useOptionalTtpcModulationService() {
  const request = useAxios(
    (axios, baseConfig, { ttpcModulationService }: ExecuteProps) =>
      axios.request<TtpcModulationServiceRootResponse>({
        ...baseConfig,
        url: ttpcModulationService,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: 'ttpc-modulation-service-root',
      onError(error) {
        if (!isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );
  const ttpcModulationService = PRISMA_CONFIG.ttpcModulationService;

  useEffect(() => {
    if (!ttpcModulationService) return;
    request.execute({ ttpcModulationService });
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return request;
}

export type OptionalTtpcModulationServiceRequest = ReturnType<
  typeof useOptionalTtpcModulationService
>;
